"use client";

import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Brain,
  BookOpenText,
  Users,
  UserCircle,
  Settings,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  FileText,
  Wallet,
  MessageSquare,
  LogOut,
  Menu,
  X,
  Video,
  NotebookPen,
  BookOpen,
  Award,
  Building2,
  TrendingUp,
  Target,
  Gift,
  Tag,
  BarChart3,
  HelpCircle,
  MessageCircle,
  Bell,
  Upload,
  MoreHorizontal,
} from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { clsx } from "clsx";
import { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "@/lib/AuthContext";
import NavigationBot from "@/components/NavigationBot";
import { Logo } from "@/components/Logo";
import ThemeSwitch from "@/components/ThemeSwitch";
import { SignOutConfirmDialog } from "@/components/SignOutConfirmDialog";
import { apiFetch, versioned } from "@/lib/api";
import styles from "./AdaptiveNav.module.css";

function NotificationBell({
  collapsed = false,
  onNavigate,
}: {
  collapsed?: boolean;
  onNavigate?: () => void;
}) {
  const router = useRouter();
  const [count, setCount] = useState(0);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const res = await apiFetch<{ count: number }>(
          versioned("/api/v1/notifications/unread-count"),
        );
        if (active) setCount(res?.count || 0);
      } catch {
        /* ignore — the bell simply shows no unread badge */
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, []);

  return (
    <button
      type="button"
      onClick={() => {
        onNavigate?.();
        router.push("/notifications");
      }}
      aria-label={`Notifications${count > 0 ? ` (${count} unread)` : ""}`}
      className={clsx(
        "relative w-full flex items-center gap-3 p-2 rounded-xl hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors",
        collapsed && "justify-center",
      )}
    >
      <span className="relative inline-flex">
        <Bell size={20} />
        {count > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </span>
      {!collapsed && (
        <span className="text-xs font-bold uppercase tracking-widest">
          Alerts
        </span>
      )}
    </button>
  );
}

export default function AdaptiveNav() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, role, logout } = useAuth();
  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const handleLogout = async () => {
    setSigningOut(true);
    try {
      await logout();
      router.replace("/auth/login");
    } finally {
      setSigningOut(false);
      setSignOutConfirmOpen(false);
    }
  };
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const displayName = user?.preferredName || user?.name || "User";
  const initials = displayName
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const subtitle = user?.assignedCourse
    ? `${user.assignedCourse}`
    : role === "admin"
      ? "Admin"
      : role === "course_rep"
        ? "Course Rep"
        : "Student";

  useEffect(() => {
    let raf = 0;
    const handleResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setIsMobile(window.innerWidth < 1024);
        setIsCollapsed(window.innerWidth >= 1024 ? false : true);
      });
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(raf);
    };
  }, []);

  const baseItems = [
    { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
    { name: "Ask AI", path: "/ask", icon: Brain },
    { name: "Learn Hub", path: "/learn", icon: BookOpenText },
    { name: "Courses", path: "/courses", icon: BookOpen },
    { name: "Study Tools", path: "/tools", icon: NotebookPen },
    { name: "Community", path: "/community", icon: MessageSquare },
    { name: "Study Groups", path: "/study-groups", icon: Users },
    { name: "My Progress", path: "/my-progress", icon: TrendingUp },
    { name: "Badges", path: "/badges", icon: Award },
    { name: "Quests", path: "/quests", icon: Target },
    { name: "Referrals", path: "/referral", icon: Gift },
    { name: "Promo", path: "/promo", icon: Tag },
    { name: "Conversations", path: "/conversations", icon: MessageCircle },
  ];

  const courseRepItems = [
    { name: "Course Rep Hub", path: "/course-rep", icon: FileText },
  ];
  const adminItems = [
    { name: "System Admin", path: "/admin", icon: ShieldCheck },
  ];

  const userItems = [
    { name: "Profile", path: "/profile", icon: UserCircle },
    { name: "Pay & Credits", path: "/pay", icon: Wallet },
    { name: "Settings", path: "/settings", icon: Settings },
  ];

  const toolItems = [
    { name: "Upload", path: "/tools/upload", icon: Upload },
    { name: "Notes", path: "/tools/notes", icon: NotebookPen },
    { name: "Flashcards", path: "/tools/flashcards", icon: BookOpen },
  ];

  const navItems = useMemo(
    () => [
      ...baseItems,
      ...(role === "course_rep" ? courseRepItems : []),
      ...(role === "admin" ? [...courseRepItems, ...adminItems] : []),
      ...userItems,
    ],
    [role],
  );

  const startsWithTools = pathname.startsWith("/tools");
  const activeTool = useMemo(
    () => toolItems.find((tool) => pathname === tool.path || pathname.startsWith(tool.path)),
    [pathname, toolItems],
  );

  // Static mobile nav items — moved outside the render path so they are not
  // recreated on every pathname/role change.
  const mobileHomeItem = { name: "Home", path: "/", icon: LayoutDashboard };
  const mobileAskItem = { name: "Ask", path: "/ask", icon: Brain };
  const mobileToolsItem = { name: "Tools", path: "/tools", icon: NotebookPen };
  const mobileAdminItem = { name: "Admin", path: "/admin", icon: ShieldCheck };
  const mobileCourseRepItem = {
    name: "Rep Hub",
    path: "/course-rep",
    icon: FileText,
  };

  const primaryItems = useMemo(() => {
    const roleItem =
      role === "admin"
        ? mobileAdminItem
        : role === "course_rep"
          ? mobileCourseRepItem
          : mobileToolsItem;

    return [
      mobileHomeItem,
      mobileAskItem,
      roleItem,
      { name: "More", path: "__more__", icon: MoreHorizontal },
    ] as Array<{ name: string; path: string; icon: any }>;
  }, [role]);

  // Sidebar navigation shown on all screen sizes
  return (
    <motion.nav
      animate={{ width: isCollapsed ? 80 : 256 }}
      transition={{ type: "spring", stiffness: 240, damping: 28 }}
      className="fixed top-0 left-0 bottom-0 h-screen z-[100] bg-card border-r border-border flex flex-col noise-texture shadow-2xl"
      style={{ willChange: "width" }}
    >
      {/* Logo & Toggle */}
      <div className="h-20 flex items-center justify-between px-6 border-b border-border">
        {!isCollapsed && (
          <Link href="/" className="hover:opacity-80 transition-opacity">
            <Logo className="h-10 w-auto" />
          </Link>
        )}
        <button
          onClick={() => {
            const newState = !isCollapsed;
            setIsCollapsed(newState);
            // Dispatch event for LayoutWrapper to sync
            window.dispatchEvent(
              new CustomEvent("sidebarToggle", { detail: newState }),
            );
          }}
          className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-foreground transition-colors ml-auto"
        >
          {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      <NavigationBot collapsed={isCollapsed} />

      {/* Nav Items */}
      <div className="flex-1 py-6 px-3 space-y-1 overflow-y-auto scrollbar-hide">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive =
            pathname === item.path ||
            (item.path === "/tools" && startsWithTools);
          return (
            <Link key={item.name} href={item.path}>
              <div
                className={clsx(
                  "relative group flex items-center gap-4 p-3 rounded-xl transition-all duration-300 cursor-pointer mb-1",
                  isActive
                    ? "bg-y text-black shadow-lg"
                    : "hover:bg-secondary text-muted-foreground hover:text-foreground",
                )}
              >
                <div className="flex-shrink-0">
                  <Icon size={22} />
                </div>
                {!isCollapsed && (
                  <motion.span
                    animate={{ opacity: 1, x: 0 }}
                    className="text-xs font-bold uppercase tracking-widest whitespace-nowrap"
                  >
                    {item.name}
                  </motion.span>
                )}
                {isCollapsed && (
                  <div className="absolute left-full ml-4 px-3 py-2 bg-foreground text-background text-xs font-bold uppercase rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[200]">
                    {item.name}
                  </div>
                )}
              </div>
            </Link>
          );
        })}
        {startsWithTools && !isCollapsed && (
          <div className="pl-6 space-y-1">
            {toolItems.map((tool) => {
              const Icon = tool.icon;
              const isActive = pathname === tool.path;
              return (
                <Link key={tool.name} href={tool.path}>
                  <div
                    className={clsx(
                      "relative group flex items-center gap-4 p-2 rounded-xl transition-all duration-300 cursor-pointer mb-1",
                      isActive
                        ? "bg-y/80 text-black shadow-md"
                        : "hover:bg-secondary text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <Icon size={18} />
                     <motion.span
                       animate={{ opacity: 1, x: 0 }}
                       className="text-xs font-bold uppercase tracking-widest whitespace-nowrap"
                     >
                       {tool.name}
                    </motion.span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer / User menu */}
      <div className="p-4 border-t border-border space-y-3">
        <div className={clsx(isCollapsed && "flex justify-center")}>
          <ThemeSwitch />
        </div>
        <NotificationBell collapsed={isCollapsed} />
        <div
          className={clsx(
            "matte-metal rounded-xl p-3 flex items-center gap-3",
            isCollapsed ? "justify-center" : "justify-start",
          )}
        >
          <div className="w-8 h-8 rounded-full bg-y flex items-center justify-center display text-black text-sm">
            {initials}
          </div>
          {!isCollapsed && (
            <div className="overflow-hidden flex-1">
              <div className="text-xs font-bold text-foreground truncate uppercase">
                {displayName}
              </div>
              <div className="text-xs text-muted-foreground font-bold uppercase">
                {subtitle}
              </div>
            </div>
          )}
          {!isCollapsed && (
            <button
              onClick={() => setSignOutConfirmOpen(true)}
              aria-label="Sign Out"
              className="p-2 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-colors"
            >
              <LogOut size={16} />
            </button>
          )}
        </div>
        {isCollapsed && (
          <button
            onClick={() => setSignOutConfirmOpen(true)}
            aria-label="Sign Out"
            className="mt-2 w-full flex items-center justify-center p-2 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-colors"
          >
            <LogOut size={16} />
          </button>
        )}
      </div>

      <SignOutConfirmDialog
        open={signOutConfirmOpen}
        onOpenChange={setSignOutConfirmOpen}
        onConfirm={handleLogout}
        confirming={signingOut}
      />
    </motion.nav>
  );
}
